import React, { useEffect, useRef, useState } from 'react'
import { ascending } from 'd3-array'

import { mapFromRawData } from 'nswidgets/src/components/table/table-mapping'
import { Legend } from 'nswidgets/src/common/legend/legend'
import { LineChartContainer } from 'nswidgets/src/components/line/line'
import { ChartFlipper } from '../chart-flipper'
import { ChartLoader } from '../chart-loader'
import { ControlBar } from '../control-bar'
import { LineBlockChart } from './line-block-chart'

import { useStyles } from './line-block.style'
import { reportLayoutService } from '../report-layout.service'
import { chartFlipperService } from '../chart-flipper.service'
import { dataFilterService } from '../data-filter.service'
import { blockDataService } from '../block-data.service'
import { lineBlockService } from './line-block.service'
import { SplitChartFilterBar } from '../split-chart-filter-bar'
import { SideBySideChart } from '../side-by-side-chart'
import { reportDataSetService } from '../../editor/report-data-set.service'
import { SideBySideChartContainer } from '../side-by-side-chart-container'

const DEFAULT_WIDTH = 1024

/**
 * This component serves as a wrapper to the LineBlockChart component, aiding with:
 * - loading and transforming block data
 * - providing the top-level control bar
 * - providing the legend
 * - orchestrating side-by-side chart rendering
 */
export const LineBlock = function ({
  blockKey,
  dataKey,
  report,
  splitChart,
  strataDefinition,
  sliderDefinition,
  filterDefinition,
  tableDefinition,
  parentControlBar,
  parentBlock,
  parentBlockSelections,
  showTableToggle = true,
  width = DEFAULT_WIDTH,
  height = width / (DEFAULT_WIDTH / 500),
  axisLabels = {},
  ...props
}) {
  const classes = useStyles()

  const rawData = useRef([])
  const calculatedXValueDomain = useRef([])
  const legendToggles = useRef()

  const [chartData, setChartData] = useState()
  const [selections, setSelections] = useState()
  const [splitChartSelections, setSplitChartSelections] = useState(
    reportDataSetService.getFilterDefinitionSplitChartSelections(filterDefinition)
  )
  const [propOverrides, setPropOverrides] = useState()

  const [showTable, setShowTable] = useState(false)
  const [selectedTableRowKey, setSelectedTableRowKey] = useState()
  const [tableData, setTableData] = useState()

  props = initializeProps(props, propOverrides)

  tableDefinition = chartFlipperService.applyTableDefinitionDefaults(tableDefinition, {
    primaryLabel: (props.axisLabels || {}).x,
    valueField: props.dataDefinition.yField,
    valueFormat: props.yFormat
  })

  useEffect(() => {
    // Strata definition changes include legend labels - need to update them in legend & table flip
    if (strataDefinition || props.yFormat) {
      updateChart(selections)
    }
  }, [strataDefinition, props.yFormat])

  function initializeProps (props, propOverrides) {
    // Add default prop values
    props = {
      dataDefinition: { emptyValue: 'NA', xField: 'x', yField: 'n' },
      isTimeSeries: false,
      ...props
    }

    // Apply any prop overrides
    return reportLayoutService.applyPropOverrides(props, propOverrides)
  }

  function onDataLoad (data) {
    let dataList = splitChart ? data : [data]

    rawData.current.length = dataList.length
    dataList.forEach((data, i) => {
      // Sort the data on x value
      if (Array.isArray(data)) {
        const x = props.dataDefinition.xField
        data = data.sort((a, b) => ascending(a[x], b[x]))
      }
      rawData.current[i] = data
    })

    legendToggles.current = {}
    updateChart(selections)
  }

  function updateChart (currentSelections) {
    if (!rawData || !rawData.current[0] || (splitChart && !rawData.current[1])) {
      return
    }

    const newChartData = []
    const newTableData = []
    rawData.current.forEach((rawData) => {
      const distinctValues = dataFilterService.getDistinctValues(
        rawData,
        [ props.dataDefinition.xField ]
      )
      let sortedDistinctXValues = distinctValues[0].values

      // The first dataset that is loaded determines the xValueDomain so that the x-axis stays consistent as dataset changes.
      if (calculatedXValueDomain.current.length === 0 && sortedDistinctXValues.length > 0) {
        calculatedXValueDomain.current = [sortedDistinctXValues[0], sortedDistinctXValues[sortedDistinctXValues.length - 1]]
      }

      const filteredData = blockDataService.filterBlockDataByKeyValue(
        rawData,
        sliderDefinition && sliderDefinition.key,
        currentSelections.slider
      )

      newChartData.push(lineBlockService.transformData(
        filteredData,
        props.dataDefinition,
        currentSelections.strata,
        strataDefinition
      ))

      if (tableDefinition) {
        const tableData = lineBlockService.transformToTableData(
          filteredData,
          props.dataDefinition,
          selections,
          strataDefinition,
          {
            isTimeSeries: props.isTimeSeries,
            xFormat: props.xFormat,
            timeFormat: props.timeFormat,
            columnsToHide: legendToggles.current
          }
        )
        const cellFormats = {
          default: tableDefinition.cellFormat
        }
        const primaryLabel = (props && props.axisLabels) ? props.axisLabels.x : ''

        newTableData.push(mapFromRawData(tableData, { cellFormats, primaryLabel }))
      }
    })
    setChartData(newChartData)
    setTableData(newTableData)
  }

  function onControlBarChange (selections, changes) {
    if (changes.tableToggle) {
      setSelectedTableRowKey(undefined)
      setShowTable(selections.tableToggle.on)
      return // table toggle change will always be alone
    }

    if (changes.props) {
      setPropOverrides(selections.props)
    }

    if (changes.slider) {
      updateChart(selections)
    } else {
      setSelections(selections)
    }
  }

  function onLineClick (closestPoints) {
    if (report.isInteractive && tableDefinition) {
      const closestPoint = (closestPoints || []).find(({ current }) => !!current)
      if (closestPoint) {
        const rowKey = lineBlockService.formatXValueForTable(closestPoint.current.x, {
          isTimeSeries: props.isTimeSeries, xFormat: props.xFormat, timeFormat: props.timeFormat
        })
        setSelectedTableRowKey(rowKey)
      } else {
        setSelectedTableRowKey()
      }
      setShowTable(true)
    }
  }

  const lineChartProps = {
    width: splitChart ? width / 2 + 32 : width,
    height: splitChart ? height / 1.25 : height,
    defaultLegendToggles: legendToggles.current,
    onClick: onLineClick,
    xValueDomain: props.xValueDomain || calculatedXValueDomain.current,
    ...props
  }

  if (splitChart) {
    lineChartProps.marginTop = 16
  }

  const missingSideBySideChartStyle = {
    margin: '36px 0 0 0',
    width: 500,
    height: 450
  }

  const tableProps = (tableDefinition || {}).props || {}
  if (splitChart) {
    tableProps.width = width / 2 - 16
  }

  return <div className="report-full-width">
    <ControlBar
      strataDefinition={strataDefinition}
      sliderDefinition={sliderDefinition}
      filterDefinition={filterDefinition}
      parentControlBar={parentControlBar}
      parentBlock={parentBlock}
      parentBlockSelections={parentBlockSelections}
      tableToggle={{ enabled: showTableToggle, on: showTable }}
      onChange={onControlBarChange} />
    <ChartLoader report={report} blockKey={blockKey} dataKey={dataKey} sliderDefinition={sliderDefinition} selections={selections} splitChart={splitChart} splitChartSelections={splitChartSelections} width={width} initialHeight={height} onDataLoad={onDataLoad}>
      {chartData && tableData && chartData.length && tableData.length ? (
        <LineChartContainer series={chartData[0]} {...lineChartProps}>
          {({ colors, legendToggles, onLegendToggle, containerRef }) => (
            <>
              {splitChart && <Legend labels={(chartData[0] || []).map((s) => (s.label || s.name))} colors={colors} toggleDefaults={legendToggles} onToggle={onLegendToggle} />}
              {splitChart && <SplitChartFilterBar filterDefinition={filterDefinition} onChange={setSplitChartSelections} className={classes.sideBySideChartFilterBar} />}
              {splitChart && chartData && chartData.length === 2 ? (
                <SideBySideChartContainer>
                  <ChartFlipper
                    isHalf
                    dataKey={dataKey}
                    enabled={showTableToggle}
                    showTable={showTable}
                    tableData={tableData[0]}
                    tableProps={tableProps}
                    selectedTableRowKey={selectedTableRowKey}
                    tableStyles={{ marginTop: 12 }}
                    tableOffset={12}
                  >
                    <div className={tableDefinition ? classes.pointer : ''}>
                      <SideBySideChart
                        className={classes.sideBySideChart}
                        data={chartData && chartData[0]}
                        missingChartStyles={missingSideBySideChartStyle}
                      >
                        <LineBlockChart
                          series={chartData[0]}
                          axisMatchSeries={chartData[1]}
                          axisLabels={axisLabels}
                          colors={colors}
                          containerRef={containerRef}
                          legendToggles={legendToggles}
                          {...lineChartProps}
                        />
                      </SideBySideChart>
                    </div>
                  </ChartFlipper>
                  <ChartFlipper
                    isHalf
                    dataKey={dataKey}
                    enabled={showTableToggle}
                    showTable={showTable}
                    tableData={tableData[1]}
                    tableProps={tableProps}
                    selectedTableRowKey={selectedTableRowKey}
                    tableStyles={{ marginTop: 12, marginLeft: 8 }}
                    tableOffset={12}
                  >
                    <div className={tableDefinition ? classes.pointer : ''}>
                      <SideBySideChart
                        className={classes.sideBySideChart}
                        data={chartData && chartData[1]}
                        missingChartStyles={{ ...missingSideBySideChartStyle, marginLeft: 24 }}
                      >
                        <LineBlockChart
                          series={chartData[1]}
                          axisMatchSeries={chartData[0]}
                          axisLabels={{ x: axisLabels.x }}
                          colors={colors}
                          containerRef={containerRef}
                          legendToggles={legendToggles}
                          {...lineChartProps}
                        />
                      </SideBySideChart>
                    </div>
                  </ChartFlipper>
                </SideBySideChartContainer>
              ) : (
                <ChartFlipper
                  dataKey={dataKey}
                  enabled={showTableToggle}
                  showTable={showTable}
                  tableData={tableData[0]}
                  tableProps={tableProps}
                  selectedTableRowKey={selectedTableRowKey}
                >
                  <div className={tableDefinition ? classes.pointer : ''}>
                    <Legend labels={(chartData[0] || []).map((s) => (s.label || s.name))} colors={colors} toggleDefaults={legendToggles} onToggle={onLegendToggle} />
                    {chartData && (
                      <LineBlockChart
                        series={chartData[0]}
                        axisLabels={axisLabels}
                        colors={colors}
                        containerRef={containerRef}
                        legendToggles={legendToggles}
                        {...lineChartProps}
                      />
                    )}
                  </div>
                </ChartFlipper>
              )}
            </>
          )}
        </LineChartContainer>
      ) : null}
    </ChartLoader>
  </div>
}
